import { useTranslation } from 'react-i18next';

import { useAuthenticatedUser, useFeature } from '@treams/treams-hooks';
import { AddIcon, ArchiveIcon, CalendarIcon, DraftsIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { PageType } from '@treams/treams-network';
import { Button, CardList, Jumbo, Sidebar, SidebarLayout } from '@treams/treams-ui';

import MutualNoteCollaborators from './MutualNoteCollaborators';
import NoteCard from './NoteCard';
import RichNoteCard from './RichNoteCard';
import NotesSidebarPeople from './one-on-ones/NotesSidebarPeople';
import OneOnOnesSummary from './one-on-ones/OneOnOnesSummary';
import { oneOnOneNoteGroupOptions, sortOneOnOneNotes } from './oneOnOneNoteGroups';

export default function ViewerNotes(props: {
	isArchived?: boolean;
	collaboratorID?: string;
	noSubject?: boolean;
	subjectID?: string;
}) {
	const { t } = useTranslation();

	const viewer = useAuthenticatedUser();
	const hasOneOnOnes = useFeature(graphql.FeatureName.OneOnOnes);

	const query = graphql.useNotesQuery({
		variables: {
			first: 10,

			filters: {
				collaboratorIDs: props.collaboratorID ? [props.collaboratorID] : [],
				subjectID: props.subjectID,
				isArchived: props.isArchived ?? false,
				hasSubject: props.noSubject ? false : undefined,
			},
		},
	});

	const subjectQuery = graphql.useUserQuery({
		variables: { id: props.subjectID ?? '' },
		skip: !props.subjectID,
	});

	const subjectTitle =
		props.subjectID === viewer.id
			? t('oneOnOnes.index.titleAboutYou')
			: subjectQuery.data
				? t('oneOnOnes.index.titleAboutSubject', {
						subject: subjectQuery.data?.user?.firstName ?? '',
					})
				: '';

	const jumboTitle = props.isArchived
		? t('oneOnOnes.sidebar.archive')
		: props.subjectID
			? subjectTitle
			: t('notes.index.title');

	const showsOneOnOnes = hasOneOnOnes && !props.noSubject;

	return (
		<SidebarLayout
			sidebar={
				<Sidebar sticky scrollable>
					{hasOneOnOnes ? (
						<>
							<Sidebar.Item
								small
								icon={<CalendarIcon />}
								title={t('oneOnOnes.sidebar.me')}
								path="/notes/me"
								exact
							/>
							<NotesSidebarPeople recurseManagees={true} excludeUserIDs={[viewer.id]} />
							<Sidebar.Separator />
							<Sidebar.Item
								small
								icon={<DraftsIcon />}
								title={t('oneOnOnes.sidebar.notes')}
								path="/notes/drafts"
								exact
							/>
						</>
					) : (
						<>
							<Sidebar.Item
								small
								icon={<DraftsIcon />}
								title={t('notes.sidebar.all')}
								path="/notes"
								exact
							/>
							<MutualNoteCollaborators />
							<Sidebar.Separator />
						</>
					)}
					<Sidebar.Item
						small
						icon={<ArchiveIcon />}
						title={hasOneOnOnes ? t('oneOnOnes.sidebar.archive') : t('notes.sidebar.archive')}
						path="/notes/archive"
						exact
					/>
				</Sidebar>
			}
		>
			{showsOneOnOnes ? (
				<Jumbo
					title={jumboTitle}
					button={
						<Button
							icon={<AddIcon />}
							title={t('oneOnOnes.index.new')}
							link={props.subjectID ? `/notes/new/subject/${props.subjectID}` : '/notes/new'}
						/>
					}
					footer={
						props.subjectID && (
							<OneOnOnesSummary
								subjectID={props.subjectID}
								noteCount={query.data?.notes.edges.length}
							/>
						)
					}
					alignItems="center"
				/>
			) : (
				<Jumbo.Editable
					hasMediaUpload
					page={PageType.Notes}
					title={t('notes.index.title')}
					description={t('notes.index.description')}
					button={<Button icon={<AddIcon />} title={t('notes.index.new')} link="/notes/new" />}
				/>
			)}

			<CardList
				translationName={showsOneOnOnes ? 'oneOnOnes.index' : 'notes.index'}
				query={query}
				path="notes"
				icon={showsOneOnOnes ? <CalendarIcon /> : <DraftsIcon />}
				renderCard={(item: graphql.Note) =>
					showsOneOnOnes ? (
						<RichNoteCard userID={viewer.id} note={item} />
					) : (
						<NoteCard userID={viewer.id} note={item} />
					)
				}
				renderSkeleton={() => (showsOneOnOnes ? <RichNoteCard.Skeleton /> : <NoteCard.Skeleton />)}
				groupOptions={showsOneOnOnes ? oneOnOneNoteGroupOptions(t) : undefined}
				sortItems={hasOneOnOnes ? sortOneOnOneNotes : undefined}
			/>
		</SidebarLayout>
	);
}
